/**
 * The realtime gateway guards (DEC-102): where the socket is ALLOWED to point.
 *
 * Pure module — no `import.meta.env`, no Vite, no pusher-js — so
 * `vite.config.ts` can run the build assertion at config time, `utils/env.ts`
 * can parse the pin once at module scope, and the Vitest suite can pin every
 * rule with plain inputs.
 *
 * The WebSocket target is SERVER-DRIVEN by contract: host, port and TLS come
 * from `GET /v1/api/me/realtime/bootstrap` (ERPat's `FUXION_WS_HOST` /
 * `FUXION_WS_PORT` / `FUXION_TLS`) and pusher-js dials exactly that. Syntry
 * has no `VITE_*` host/port/TLS override — one would recreate the 2026-09-16
 * `.env.production.local` incident class with a *silent* failure mode, because
 * a refused or unreachable upgrade is a bare DOM `error` pusher-js swallows and
 * retries: the app's `connection.bind('error')` never fires and the card just
 * reads "Reconnecting…" forever. What Syntry gets instead:
 *
 *  - `gateway_loopback` — the ONE hard refusal, no env key needed: the
 *    bootstrap names a loopback `ws_host` while the API base is a non-loopback
 *    host. That is "a dev `.env` copied to production"; it can never be a
 *    legitimate deployment (every device would dial itself), it is inert in
 *    dev and tests (the API base is loopback there), and it needs no socket to
 *    detect.
 *  - `gateway_unexpected` — the ADVISORY pin: `VITE_REALTIME_GATEWAY_ORIGINS`
 *    (optional, comma list of exact `ws(s)://host[:port]` origins; production
 *    `wss://pushit.erpat.app`) is compared against the normalised bootstrap.
 *    A mismatch is named — once in the dev console, and as a reason line on
 *    the Live-updates card — and the app STILL dials; a successful connect
 *    clears it (the `origin_mismatch` precedent). A wrong pin therefore costs
 *    nothing; a wrong server produces a named line instead of an endless
 *    "Reconnecting…". It is misconfiguration detection, not security — the
 *    enforcement points stay the gateway's Origin allowlist and the
 *    per-channel HMAC.
 *
 * Deliberately NO `gateway_insecure` refusal: `force_tls:false` + port 443
 * works on the PWA and Android (pusher-js forces TLS on an `https:` page) and
 * refusing it would turn an iOS-only outage into a four-surface one; that
 * sanity check belongs server-side (`php erpat realtime:check`).
 */
import { isLoopbackHostname } from './api-base-url';

/** The bootstrap's `pusher` block, reduced to what decides the socket URL. */
export interface GatewaySettings {
  wsHost: string;
  wsPort: number;
  wssPort: number;
  forceTls: boolean;
}

/** One of the two DEC-102 verdicts (see the module docblock). */
export type GatewayRefusalCode = 'gateway_loopback' | 'gateway_unexpected';

export interface GatewayRefusal {
  code: GatewayRefusalCode;
  /** The normalised origin the bootstrap describes, or `null` when it is unusable. */
  origin: string | null;
}

export interface GatewayOriginPins {
  /** Normalised, de-duplicated `ws(s)://host[:port]` origins, in declaration order. */
  origins: string[];
  /** One line per entry that was dropped, naming the entry and the rule. */
  problems: string[];
}

/** What pusher-js dials: `/^[a-z0-9.-]+$/` after lower-casing — the same charset ERPat's `realtimeOriginsFor()` accepts. */
const HOST_PATTERN = /^[a-z0-9.-]+$/;

/**
 * Mirror of pusher-js `shouldUseTLS()` (`core/config.ts`): a page served over
 * `https:` ALWAYS uses TLS (and dials `wsHost:wssPort`); any other page —
 * `http:` in dev, `capacitor:` in the iOS shell — follows the bootstrap's
 * `force_tls`. Key on the PAGE protocol (`window.location.protocol`), which is
 * what pusher-js reads — never `isSecureContext`.
 */
export function gatewayUsesTls(forceTls: boolean, pageProtocol: string): boolean {
  return pageProtocol === 'https:' || forceTls;
}

/**
 * The `ws(s)://host[:port]` origin pusher-js will dial for `settings` on a
 * page with `pageProtocol`, normalised the way ERPat's
 * `SecureHeadersMiddleware::realtimeOriginsFor()` and the pin parser normalise
 * theirs: host lower-cased and charset-checked, the TLS-appropriate port, and
 * the default port (443 for `wss`, 80 for `ws`) elided — so
 * `{pushit.erpat.app, 443, 443, true}` and the pin `wss://pushit.erpat.app:443`
 * both read `wss://pushit.erpat.app`. `null` when the host or port is unusable.
 */
export function gatewayOriginFor(settings: GatewaySettings, pageProtocol: string): string | null {
  const host = settings.wsHost.trim().toLowerCase();
  if (host === '' || !HOST_PATTERN.test(host)) return null;
  const tls = gatewayUsesTls(settings.forceTls, pageProtocol);
  const port = tls ? settings.wssPort : settings.wsPort;
  if (!Number.isInteger(port) || port < 1 || port > 65_535) return null;
  const scheme = tls ? 'wss' : 'ws';
  const defaultPort = tls ? 443 : 80;
  return port === defaultPort ? `${scheme}://${host}` : `${scheme}://${host}:${port}`;
}

/**
 * Parse `VITE_REALTIME_GATEWAY_ORIGINS`: a comma list of exact
 * `ws(s)://host[:port]` origins. Each entry is trimmed and canonicalised
 * through `new URL()` (host lower-cased, a default port elided — so
 * ` WSS://PushIt.ERPat.app:443/ ` is `wss://pushit.erpat.app`), and must be a
 * BARE origin: `ws:`/`wss:` only, no path beyond `/`, no query, no fragment,
 * no credentials. Entries that break a rule land in `problems` (entry + rule,
 * never anything else) and are dropped; empty entries between commas are
 * ignored; duplicates collapse. Unset or blank → no pins, no problems.
 */
export function parseGatewayOriginPins(raw: string | undefined): GatewayOriginPins {
  const origins: string[] = [];
  const problems: string[] = [];
  const value = (raw ?? '').trim();
  if (value === '') return { origins, problems };

  for (const entry of value.split(',')) {
    const token = entry.trim();
    if (token === '') continue;

    let url: URL;
    try {
      url = new URL(token);
    } catch {
      problems.push(`"${token}" is not an absolute ws:// or wss:// URL`);
      continue;
    }
    if (url.protocol !== 'ws:' && url.protocol !== 'wss:') {
      problems.push(`"${token}" must use the ws:// or wss:// scheme`);
      continue;
    }
    if (url.username !== '' || url.password !== '') {
      problems.push(`"${token}" must not carry credentials`);
      continue;
    }
    if ((url.pathname !== '/' && url.pathname !== '') || url.search !== '' || url.hash !== '') {
      problems.push(`"${token}" must be a bare origin (no path, query or fragment)`);
      continue;
    }
    // Built by hand rather than read off `url.origin`: the parser has already
    // lower-cased the host and dropped a default port, and this keeps the
    // shape identical to `gatewayOriginFor()` whatever the runtime's origin
    // serialisation does for `ws:` URLs.
    const origin = `${url.protocol}//${url.hostname}${url.port === '' ? '' : `:${url.port}`}`;
    if (!origins.includes(origin)) origins.push(origin);
  }
  return { origins, problems };
}

/**
 * The DEC-102 verdict for one bootstrap, or `null` when nothing is wrong.
 *
 * `gateway_loopback` (hard — the caller refuses to dial): `apiHostname` is a
 * real, non-loopback host while `settings.wsHost` is loopback. Evaluated
 * FIRST, before any pin, and with no pin at all. An unparseable
 * `apiHostname` (`null`) keeps the rule inert rather than guessing.
 *
 * `gateway_unexpected` (advisory — the caller names it and still dials): one
 * or more `pins` are configured and the normalised bootstrap origin is not
 * among them (an unusable origin counts as "not among them").
 */
export function gatewayRefusal(input: {
  settings: GatewaySettings;
  pageProtocol: string;
  apiHostname: string | null;
  pins: readonly string[];
}): GatewayRefusal | null {
  const origin = gatewayOriginFor(input.settings, input.pageProtocol);
  const api = (input.apiHostname ?? '').trim();
  if (api !== '' && !isLoopbackHostname(api) && isLoopbackHostname(input.settings.wsHost)) {
    return { code: 'gateway_loopback', origin };
  }
  if (input.pins.length > 0 && (origin === null || !input.pins.includes(origin))) {
    return { code: 'gateway_unexpected', origin };
  }
  return null;
}

/**
 * Why `value` is not a pin a PRODUCTION bundle should carry, or `null` when it
 * is fine. Unset / blank is fine — the pin is optional and a gateway host
 * change must never force a Syntry release. When it IS set, every entry must
 * parse (see {@link parseGatewayOriginPins}), name a non-loopback host, and
 * use `wss://` — a deployed bundle can only ever reach a public TLS gateway.
 */
export function gatewayOriginsPinProblem(value: string | undefined): string | null {
  const raw = (value ?? '').trim();
  if (raw === '') return null;

  const { origins, problems } = parseGatewayOriginPins(raw);
  const firstProblem = problems[0];
  if (firstProblem !== undefined) {
    return `VITE_REALTIME_GATEWAY_ORIGINS has an unusable entry: ${firstProblem}`;
  }
  for (const origin of origins) {
    const url = new URL(origin);
    if (isLoopbackHostname(url.hostname)) {
      return `VITE_REALTIME_GATEWAY_ORIGINS points at the local machine: "${origin}"`;
    }
    if (url.protocol === 'ws:') {
      return `VITE_REALTIME_GATEWAY_ORIGINS names a plain ws:// gateway, but a deployed bundle can only reach a wss:// one: "${origin}"`;
    }
  }
  return null;
}

/**
 * Throw unless `value` is a pin a deployed bundle could sensibly carry. Call
 * this for a production-mode `vite build` only, and note it is SILENT when the
 * variable is unset — unlike `assertDeployableApiBaseUrl()`, an absent pin is
 * the normal dev / `verify` / "no pin wanted" case, not a misconfiguration.
 */
export function assertDeployableGatewayOrigins(value: string | undefined): void {
  const problem = gatewayOriginsPinProblem(value);
  if (problem === null) return;

  throw new Error(
    [
      `[syntry] Refusing to build a production bundle: ${problem}.`,
      'VITE_REALTIME_GATEWAY_ORIGINS is the ADVISORY gateway pin (DEC-102): the',
      'origin(s) the realtime bootstrap is expected to name, which a deployed',
      "bundle compares against on every visitor's device. Check for a stray",
      '.env.production.local (Vite loads it above .env.production) and set',
      'VITE_REALTIME_GATEWAY_ORIGINS=wss://pushit.erpat.app — or leave it unset;',
      'the pin is optional. For a local preview-server verification build use',
      '`npm run build:verify` (the `verify` Vite mode, see .env.verify).',
    ].join('\n'),
  );
}
